/**
 * UnansweredWarningV2 - Warning modal for unanswered questions
 *
 * Features:
 * - Shows unanswered question count
 * - Option to continue or go back
 * - Highlights unanswered questions
 */

import { memo, useCallback, useEffect } from 'react'
import {
  useChallengeV2 as useChallengeStoreV2,
  selectUnansweredCount,
  selectMarkedCount
} from '../../../hooks/useChallenge'

// Warning icon
const WarningIcon = () => (
  <svg className="w-16 h-16" viewBox="0 0 64 64" fill="none">
    <circle cx="32" cy="32" r="28" className="fill-amber-100 dark:fill-amber-900/30" />
    <path
      d="M32 20v16M32 44h.01"
      className="stroke-amber-500"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const UnansweredWarningV2 = memo(() => {
  // Store state
  const showUnansweredWarning = useChallengeStoreV2((state) => state.showUnansweredWarning)
  const unansweredCount = useChallengeStoreV2(selectUnansweredCount)
  const markedCount = useChallengeStoreV2(selectMarkedCount)
  const questionStatuses = useChallengeStoreV2((state) => state.questionStatuses)

  // Store actions
  const closeUnansweredWarning = useChallengeStoreV2((state) => state.closeUnansweredWarning)
  const handleNavigateToQuestion = useChallengeStoreV2((state) => state.handleNavigateToQuestion)

  // Find first unanswered question
  const firstUnansweredIndex = questionStatuses.findIndex((s) => !s.answered)

  // Handle go to unanswered
  const handleGoToUnanswered = useCallback(() => {
    if (firstUnansweredIndex >= 0) {
      handleNavigateToQuestion(firstUnansweredIndex)
    }
    closeUnansweredWarning()
  }, [firstUnansweredIndex, handleNavigateToQuestion, closeUnansweredWarning])

  // Prevent body scroll
  useEffect(() => {
    if (showUnansweredWarning) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [showUnansweredWarning])

  if (!showUnansweredWarning) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="warning-title"
      aria-describedby="warning-desc"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={closeUnansweredWarning}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-8 text-center animate-[modalIn_0.25s_ease-out]">
        {/* Warning icon */}
        <div className="flex justify-center mb-6">
          <WarningIcon />
        </div>

        {/* Title */}
        <h2 id="warning-title" className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Unanswered Questions
        </h2>

        <p id="warning-desc" className="text-gray-600 dark:text-gray-400 mb-6">
          You have{' '}
          <span className="font-bold text-amber-600 dark:text-amber-400">{unansweredCount}</span>{' '}
          unanswered
          {unansweredCount === 1 ? ' question' : ' questions'}.
          {markedCount > 0 && <span className="block mt-1">({markedCount} marked for review)</span>}
        </p>

        {/* Info text */}
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 mb-6">
          <p className="text-sm text-amber-700 dark:text-amber-300">
            You must answer all questions before submitting this stage.
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col gap-3">
          <button
            onClick={handleGoToUnanswered}
            className="w-full px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-200"
          >
            Go to Question {firstUnansweredIndex + 1}
          </button>
          <button
            onClick={closeUnansweredWarning}
            className="w-full px-6 py-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-xl transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
})

UnansweredWarningV2.displayName = 'UnansweredWarningV2'

export default UnansweredWarningV2
